import { $http } from '@/utils/https'
import type { ActionContext } from 'vuex'
import type { RootState } from '@/store'
import type {
  GiftSale,
  GiftVariant,
  SelectedGift,
  GiftPayloadItem,
} from '@/types/gifts'
import {
  buildBasketHash,
  readGiftSelection,
  writeGiftSelection,
  clearGiftSelection,
  GIFT_STORAGE_KEY,
} from '@/helpers/giftStorage'

interface State {
  availableSales: GiftSale[]
  selected: SelectedGift[]
  isLoading: boolean
  lastCheckedBasketHash: string
}

const initialStored = readGiftSelection()

const state: State = {
  availableSales: [],
  selected: initialStored?.selectedGifts ?? [],
  isLoading: false,
  lastCheckedBasketHash: initialStored?.basketHash ?? '',
}

function sumSelectedInSale(selected: SelectedGift[], saleId: number): number {
  return selected
    .filter((g) => g.saleId === saleId)
    .reduce((acc, g) => acc + g.count, 0)
}

function reconcileSelection(
  selected: SelectedGift[],
  sales: GiftSale[],
): SelectedGift[] {
  const result: SelectedGift[] = []
  for (const sale of sales) {
    const picks = selected.filter((g) => g.saleId === sale.saleId)
    let remainingForSale = sale.maxCount
    for (const pick of picks) {
      if (remainingForSale <= 0) break
      const variant = sale.gifts.find(
        (v) => v.productPriceId === pick.productPriceId,
      )
      if (!variant) continue
      // Cap against absolute maxQuantity only. `givenQuantity` may already
      // include this very pick (when the gift is already in the basket), so
      // subtracting it here would drop a valid existing selection and force
      // the user to re-pick. `givenQuantity` still drives the "N left to add"
      // hint in the select-gift popup, where that meaning is correct.
      const variantLimit =
        variant.maxQuantity === null || variant.maxQuantity === undefined
          ? Number.POSITIVE_INFINITY
          : variant.maxQuantity
      const allowed = Math.min(pick.count, remainingForSale, variantLimit)
      if (allowed > 0) {
        result.push({
          saleId: sale.saleId,
          productPriceId: pick.productPriceId,
          count: allowed,
        })
        remainingForSale -= allowed
      }
    }
  }
  return result
}

const getters = {
  availableSales: (s: State): GiftSale[] => s.availableSales,
  selectedGifts: (s: State): SelectedGift[] => s.selected,
  isGiftLoading: (s: State): boolean => s.isLoading,
  allowedGiftCount: (s: State): number =>
    s.availableSales.reduce((acc, sale) => acc + (sale.maxCount || 0), 0),
  selectedGiftCount: (s: State): number =>
    s.selected.reduce((acc, g) => acc + g.count, 0),
  hasUnselectedGifts: (s: State, g: any): boolean => {
    if (s.availableSales.length === 0) return false
    return g.selectedGiftCount < g.allowedGiftCount
  },
  isGiftSelectionValid: (s: State): boolean => {
    if (s.selected.length === 0) return true
    if (s.availableSales.length === 0) return false
    for (const sale of s.availableSales) {
      const sumInSale = sumSelectedInSale(s.selected, sale.saleId)
      if (sumInSale > sale.maxCount) return false
    }
    for (const pick of s.selected) {
      const sale = s.availableSales.find((v) => v.saleId === pick.saleId)
      if (!sale) return false
      const variant = sale.gifts.find(
        (v) => v.productPriceId === pick.productPriceId,
      )
      if (!variant) return false
      if (variant.maxQuantity !== null && variant.maxQuantity !== undefined) {
        if (pick.count > variant.maxQuantity) return false
      }
    }
    return true
  },
  giftPayloadItems: (s: State, g: any): GiftPayloadItem[] => {
    if (!g.isGiftSelectionValid) return []
    return s.selected
      .filter((item) => item.count > 0)
      .map((item) => ({
        product_price_id: item.productPriceId,
        count: item.count,
        is_free: true,
        sale_id: item.saleId,
      }))
  },
}

const mutations = {
  SET_AVAILABLE_SALES(state: State, sales: GiftSale[]) {
    state.availableSales = Array.isArray(sales) ? sales : []
  },
  SET_SELECTED(state: State, selected: SelectedGift[]) {
    state.selected = Array.isArray(selected) ? selected : []
  },
  SET_GIFT_LOADING(state: State, status: boolean) {
    state.isLoading = status
  },
  SET_LAST_BASKET_HASH(state: State, hash: string) {
    state.lastCheckedBasketHash = hash || ''
  },
  RESET_GIFTS(state: State) {
    state.availableSales = []
    state.selected = []
    state.lastCheckedBasketHash = ''
  },
}

const actions = {
  HYDRATE_GIFTS({ commit }: ActionContext<State, RootState>) {
    const stored = readGiftSelection()
    if (stored) {
      commit('SET_SELECTED', stored.selectedGifts)
      commit('SET_LAST_BASKET_HASH', stored.basketHash)
    }
  },

  async CHECK_GIFT({
    commit,
    state,
    rootGetters,
  }: ActionContext<State, RootState>) {
    const rawBasket = rootGetters['basket/basket'] || []
    const basket = rawBasket.filter((item: any) => !item?.isPresent && !item?.isFree)
    const basketHash = buildBasketHash(basket)

    if (!basket.length) {
      commit('SET_AVAILABLE_SALES', [])
      commit('SET_SELECTED', [])
      commit('SET_LAST_BASKET_HASH', '')
      clearGiftSelection()
      return
    }

    commit('SET_GIFT_LOADING', true)
    try {
      const productsMap: Record<string, number> = {}
      basket.forEach((item: any) => {
        const id = item?.select_type?.id
        const count = item?.select_count
        if (id != null && count > 0) productsMap[String(id)] = count
      })

      const response = await $http.get('v1/products/check-gift', {
        params: productsMap,
        paramsSerializer: (params: Record<string, any>) => {
          const parts: string[] = []
          Object.keys(params).forEach((k) => {
            parts.push(
              `products[${encodeURIComponent(k)}]=${encodeURIComponent(
                String(params[k]),
              )}`,
            )
          })
          return parts.join('&')
        },
      })

      const payload = response.data
      const sales: GiftSale[] = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : []

      commit('SET_AVAILABLE_SALES', sales)

      const basketGifts: SelectedGift[] = rawBasket
        .filter(
          (item: any) =>
            item?.isFree &&
            item?.saleId != null &&
            item?.select_type?.id != null &&
            item?.select_count > 0,
        )
        .map((item: any) => ({
          saleId: item.saleId,
          productPriceId: item.select_type.id,
          count: item.select_count,
        }))

      const stored = readGiftSelection()
      const storedSelection =
        stored && stored.basketHash === basketHash ? stored.selectedGifts : []
      const previousSelection =
        basketGifts.length > 0 ? basketGifts : storedSelection
      const reconciled = reconcileSelection(previousSelection, sales)

      commit('SET_SELECTED', reconciled)
      commit('SET_LAST_BASKET_HASH', basketHash)
      writeGiftSelection(basketHash, reconciled)
    } catch (e) {
      console.error('CHECK_GIFT failed', e)
      commit('SET_AVAILABLE_SALES', [])
    } finally {
      commit('SET_GIFT_LOADING', false)
    }
  },

  APPLY_SELECTED_GIFTS(
    { commit, state }: ActionContext<State, RootState>,
    selected: SelectedGift[],
  ) {
    const reconciled = reconcileSelection(selected || [], state.availableSales)
    commit('SET_SELECTED', reconciled)
    writeGiftSelection(state.lastCheckedBasketHash, reconciled)
    return reconciled
  },

  REMOVE_SELECTED_GIFT(
    { commit, state }: ActionContext<State, RootState>,
    { productPriceId, saleId }: { productPriceId: number; saleId: number | null },
  ) {
    const next = state.selected.filter(
      (g) => !(g.productPriceId === productPriceId && (saleId == null || g.saleId === saleId)),
    )
    commit('SET_SELECTED', next)
    writeGiftSelection(state.lastCheckedBasketHash, next)
  },

  RESET_SELECTED_GIFTS({ commit }: ActionContext<State, RootState>) {
    commit('RESET_GIFTS')
    clearGiftSelection()
  },
}

export function wireGiftsCrossTab(store: any) {
  if (typeof window === 'undefined') return
  window.addEventListener('storage', (event) => {
    if (event.key !== GIFT_STORAGE_KEY) return
    try {
      const parsed = event.newValue ? JSON.parse(event.newValue) : null
      if (!parsed) {
        store.commit('gifts/SET_SELECTED', [])
        store.commit('gifts/SET_LAST_BASKET_HASH', '')
      } else {
        store.commit('gifts/SET_SELECTED', parsed.selectedGifts || [])
        store.commit('gifts/SET_LAST_BASKET_HASH', parsed.basketHash || '')
      }
    } catch (e) {}
  })
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
